import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Col, Container, Navbar, Offcanvas, Row } from 'react-bootstrap';
import StyledProfileDropDown from '../components/styledComponents/StyledProfileDropDown';
import useAuth from '../../hooks/useAuth';
import {
  removeCredentials,
  selectBasicInformation,
  setUserBasicInfo,
} from '../../features/auth/authSlice';
import LogoutIcon from '../components/svgIcons/LogoutIcon';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import HelpIcon from '../components/svgIcons/HelpIcon';
import StyledText from '../components/styledComponents/StyledText';
import BoostFloralAvatarIcon from '../components/svgIcons/BoostFloralAvatarIcon';
import ProfileCircle from '../components/svgIcons/ProfileCircle';
import Setting2 from '../components/svgIcons/Setting2';
import { resetTheme } from '../services/themeSlice';
import StyledIconBox from '../components/styledComponents/StyledIconBox';
import { communicationRoutes, RoutesData } from '../../RoutesHelper';
import MenuIcon from '../components/svgIcons/MenuIcon';
import { useSelector } from 'react-redux';
import { openHelpSection } from '../services/helpSectionSlice';
import StyledButton from '../components/styledComponents/StyledButton';
import { openSubscriptionModal } from '../services/subscriptionModalSlice';
import GettingStartedWizard from '../components/GettingStartedWizard';
import {
  useGetOnboardingChecklistQuery,
  useGetPermissionsQuery,
} from '../../features/dashboard/dashboardApiSlice';
import { SUBSCRIPTIONSPLANS } from '../../utils/Constants';
import { useGetUserDetailsQuery } from '../../features/profile/profileApiSlice';
import { setPermissions, setUserPermissions } from '../services/Authorization';
import { isAuthorized } from '../../utils/helper';

const Header = ({
  openSideMenu,
  setOpenSideMenu,
  completed,
  total,
  percentage,
  gettingStartedOpen,
  setGettingStartedOpen,
}) => {
  const expand = 'sm';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userPermissions = useSelector(
    (state) => state?.authorization?.userPermissions,
  );

  const has_permission_to_view_delivery_order = isAuthorized(
    userPermissions,
    'can_view_deliveries',
  );
  const { data: preLiveChecks, isFetching } = useGetOnboardingChecklistQuery();
  const { data: floristDetails } = useGetUserDetailsQuery(null, {
    forceRefetch: true,
    refetchOnMountOrArgChange: true,
  });
  const { data: permissions } = useGetPermissionsQuery(null, {
    forceRefetch: true,
    refetchOnMountOrArgChange: true,
  });

  const authorizations = useSelector((state) => state.authorization);

  const basicInfo = useSelector(selectBasicInformation);
  const { id, orderId, groupId, name } = useParams();
  const location = useLocation();
  const [breadCrumb, setBreadCrumb] = useState({
    heading: '',
    breadCrumbComponent: null,
    hideBreadCrumb: false,
  });
  const pathnameParts = location.pathname.split('/');
  let firstPartOfPath = pathnameParts.slice(1, -1).join('/');

  const _routes = [
    ...RoutesData.filter((route) => {
      // If only allowedRoleIds is present, check that and ignore allowedRoles
      if (route.allowedRoleIds && !route.allowedRoles) {
        return route.allowedRoleIds.some((roleId) => {
          return user?.roles?.some((userRole) => userRole.id === roleId);
        });
      }

      // If only allowedRoles is present, check that and ignore allowedRoleIds
      if (route.allowedRoles && !route.allowedRoleIds) {
        return route.allowedRoles.some((role) => user?.[role]);
      }

      // If both allowedRoleIds and allowedRoles are present, check both
      if (route.allowedRoleIds && route.allowedRoles) {
        const allowedRolesIdsMatch = route.allowedRoleIds.some((roleId) => {
          return user?.roles?.some((userRole) => userRole.id === roleId);
        });

        const allowedRolesMatch = route.allowedRoles.some(
          (role) => user?.[role],
        );

        return allowedRolesIdsMatch && allowedRolesMatch; // Require both to match
      }

      // If neither allowedRoleIds nor allowedRoles are present, return true by default
      return true;
    }),
    ...communicationRoutes,
  ];

  useEffect(() => {
    if (permissions && authorizations?.permissions?.length === 0) {
      dispatch(setPermissions(permissions ?? []));
    }
  }, [permissions]);

  useEffect(() => {
    let item = {};
    const pathnameParts = location.pathname.split('/');
    if ((id || orderId || groupId) && pathnameParts.length >= 2) {
      if (
        /^\/admin\/community\/everything\/\d+\/\w+$/.test(location.pathname)
      ) {
        const regex = /^\/(\w+\/\w+\/\w+)\/\d+\/\w+$/;
        const match = location.pathname.match(regex);

        if (match) {
          firstPartOfPath = match[1];
        }
      }
      item = _routes.find(
        (route) =>
          route?.breadCrumbPath ===
          `/${firstPartOfPath}/:${id ? 'id' : orderId ? 'orderId' : 'groupId'}${
            name ? '/:name' : ''
          }`,
      );
    } else {
      item = _routes.find(
        (route) => route?.breadCrumbPath === location.pathname,
      );
    }

    setBreadCrumb({
      heading: item?.breadCrumbHeading || '',
      breadCrumbComponent: item?.breadCrumbComponent,
      hideBreadCrumb: item?.hideBreadCrumb || false,
    });
  }, [location.pathname]);

  const firstMenu = [
    {
      name: 'Profile',
      icon: <ProfileCircle />,
      to: 'profile',
    },
    {
      name: 'Settings',
      icon: <Setting2 />,
      to: 'settings',
    },
  ];

  const userMenuItems = [
    {
      name: 'Logout',
      icon: <LogoutIcon />,
      subItems: [
        {
          name: 'Logout',
          icon: <LogoutIcon />,
        },
      ],
      action: () => {
        dispatch(removeCredentials());
        dispatch(resetTheme());
        navigate('/');

        // Reload the window
        window.location.reload();
      },
    },
  ];

  const handleOpenHelpSection = () => {
    dispatch(
      openHelpSection({
        show: true,
      }),
    );
  };

  const handleOpenSubscriptionModal = () => {
    dispatch(openSubscriptionModal());
  };

  useEffect(() => {
    if (floristDetails) {
      dispatch(
        setUserBasicInfo({
          values: {
            firstName: floristDetails.firstName || '',
            lastName: floristDetails.lastName || '',
            imageUrl:
              floristDetails.imageUrl ||
              'https://dev-boostfloral-images.s3.amazonaws.com/userDefault.png',
            usingWebHosting:
              floristDetails?.floristDTO?.usingWebHosting || false,
            website: floristDetails?.floristDTO?.website || '',
            subdomainUrl: floristDetails?.floristDTO?.subdomainUrl || '',
          },
        }),
      );
    }

    if (floristDetails && authorizations?.userPermissions?.length === 0) {
      dispatch(setUserPermissions(floristDetails?.assignedResources ?? []));
    }
  }, [floristDetails]);

  return (
    <StyledHeader>
      <Container fluid>
        <Row>
          <Col className="p-0">
            <Navbar key={expand}>
              <Navbar.Toggle
                aria-controls={`offcanvasNavbar-expand-${expand}`}
              />
              <Navbar.Offcanvas
                id={`offcanvasNavbar-expand-${expand}`}
                aria-labelledby={`offcanvasNavbarLabel-expand-${expand}`}
                placement="end"
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title id={`offcanvasNavbarLabel-expand-${expand}`}>
                    BoostFloral
                  </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="justify-content-end flex-column-reverse flex-md-row offcanvasBody">
                  <StyledHeadingContainer
                    hasHeading={breadCrumb.breadCrumbComponent}
                    className={`menu-details ${
                      breadCrumb.hideBreadCrumb ? 'd-none' : ''
                    } py-0`}
                  >
                    <div className="main_heading mb-0">
                      {breadCrumb.heading === 'Create your website'
                        ? basicInfo?.usingWebHosting
                          ? 'Edit your website'
                          : 'Create your website'
                        : breadCrumb.heading}
                    </div>
                    {breadCrumb.heading === 'Create your website'
                      ? basicInfo?.usingWebHosting
                        ? 'Website / Edit your website'
                        : 'Website / Create your website'
                      : breadCrumb.breadCrumbComponent}
                  </StyledHeadingContainer>
                  <StyledText
                    color="dark40"
                    className={`d-flex align-items-center justify-content-between menu-header ${
                      breadCrumb.hideBreadCrumb ? 'border-0' : ''
                    }`}
                    as="div"
                  >
                    {/*646567*/}
                    <div className="d-flex align-items-center d-md-none">
                      {/*          
                        <BoostFloralAvatarIcon
                          size="34.5px"
                          className={`me-2 me-md-4 ms-1 menu-avatar`}
                        />
                     */}

                      <StyledText
                        color="icon"
                        className="cursor-pointer ms-1 mb-0"
                        onClick={() => setOpenSideMenu(!openSideMenu)}
                      >
                        <MenuIcon />
                      </StyledText>
                    </div>
                    <div className="d-flex align-items-center">
                      {preLiveChecks?.floristSubscriptionDTO?.subType ===
                        SUBSCRIPTIONSPLANS.STARTER.toLowerCase() && (
                        <StyledButton
                          className="px-4 py-2 mx-4"
                          onClick={handleOpenSubscriptionModal}
                        >
                          <StyledText
                            $fontSize="clamp(14px, 3vw, 18px)"
                            $fontWeight="500"
                            color="white"
                          >
                            Upgrade
                          </StyledText>
                        </StyledButton>
                      )}

                      <StyledIconBox
                        size={'48px'}
                        $bgColor={'dark95'}
                        $color={'dark40'}
                        $circle
                        fill="none"
                        className="mx-1 cursor-pointer"
                      >
                        <HelpIcon
                          className="cursor-pointer"
                          size={25}
                          onClick={handleOpenHelpSection}
                        />
                      </StyledIconBox>

                      {/* <StyledIconBox
                        size={'48px'}
                        fill="none"
                        $bgColor={'dark95'}
                        $circle
                        $color={'dark40'}
                        className="me-3 cursor-pointer"
                      >
                        <NotificationBellIcon className="mx-4 cursor-pointer" />
                      </StyledIconBox> */}

                      {completed !== total && !isFetching && (
                        <StyledIconBox
                          size={'50px'}
                          $bgColor={'dark95'}
                          $color={'dark40'}
                          $circle
                          fill="none"
                          className="me-2 cursor-pointer"
                        >
                          <GettingStartedWizard
                            open={gettingStartedOpen}
                            setOpen={setGettingStartedOpen}
                            tasks={total}
                            completed={completed}
                            percentage={percentage}
                            isFetching={isFetching}
                            preLiveChecks={preLiveChecks}
                          />
                        </StyledIconBox>
                      )}

                      <StyledProfileDropDown
                        firstMenu={firstMenu}
                        menuItems={userMenuItems}
                        profile={floristDetails}
                        toggleClassName={'bg-transparent p-0'}
                      />
                    </div>
                  </StyledText>
                </Offcanvas.Body>
                <style>
                  {`
                    .offcanvasBody {
                    padding : 0px !important
                    }
                    `}
                </style>
              </Navbar.Offcanvas>
            </Navbar>
          </Col>
        </Row>
      </Container>
    </StyledHeader>
  );
};
const StyledHeadingContainer = styled.div`
  font-family: 'Instrument Sans';
  display: flex;
  width: 100%;
  flex-direction: column;
  .main_heading {
    width: 100%;
    display: flex;
    padding: ${({ hasHeading }) => (!hasHeading ? '8.2px 0' : '0')};
    font-size: 30px;
    font-weight: 600;
    color: ${({ theme }) => theme.boldHeading};
  }

  .breadcrumb {
    margin-bottom: 0;
  }
  .breadcrumb-item {
    font-size: 14px;
    line-height: normal;
    font-weight: 400;
    color: ${({ theme }) => theme.bodyMedium};
    a {
      color: ${({ theme }) => theme.bodyMedium};
      text-decoration: none;
      &.active {
        color: ${({ theme }) => theme.heading};
      }
    }
    + .breadcrumb-item::before {
      color: ${({ theme }) => theme.bodyMedium};
    }
    ${({ theme }) => css`
      @media (max-width: ${theme?.breakpoints?.sm}px) {
        font-size: 14px;
      }
    `}
  }
`;
const StyledHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 111;
  border-bottom: 1.5px solid ${({ theme }) => theme.dark70};
  background-color: ${({ theme }) => theme.white};
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px; /* Adjust the width of the shadow as needed */
    height: 100%;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.04);
    z-index: -1;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.03) 0%,
      transparent 100%
    ); /* Adjust the stops of the gradient */
  }
  .active {
    font-weight: 500 !important;
  }

  .menu-transition {
    transition: all 0.3s ease-in-out;
  }

  .navbar {
    transition: all 0.3s ease;
    background-color: ${({ theme }) => theme.transparent};
  }

  .navbar-light {
    padding: 20px 40px;
  }

  .logo-container {
    display: flex;
    align-items: center;
  }

  .avatar-icon {
    transition: transform 1s ease;
  }

  .sidebar-opener-icon {
    transition: transform 1s ease;
  }

  .avatar-icon {
    transform: scale(0);
  }

  .sidebar-opener-icon {
    transform: rotate(180deg);
  }
  ${({ theme }) => css`
    @media (max-width: 767px) {
      .navbar-light {
        padding: 0;
      }
      .menu-details {
        padding: 20px;
        .main_heading {
          font-size: 24px;
        }
      }

      .menu-header {
        border-bottom: 1.5px solid ${({ theme }) => theme.dark70};
        padding: 20px;
      }
    }
  `}

  @media (max-width: 425px) {
    .menu-header {
      padding: 10px !important;
    }
  }
`;

export default Header;
